// Lightweight IndexedDB key-value helper with sync cache facade
// No fallback to localStorage. Includes basic error handling.

const DB_NAME = 'eatsure_kiosk_db';
const STORE_NAME = 'app_store';

let __dbPromise = null;
const __cache = new Map(); // in-memory cache for sync reads
const __hasIDB = typeof window !== 'undefined' && !!window.indexedDB;

function openDB() {
  if (!__hasIDB) {
    return Promise.reject(new Error('IndexedDB is not available in this environment'));
  }
  if (__dbPromise) return __dbPromise;
  __dbPromise = new Promise((resolve, reject) => {
    try {
      const request = window.indexedDB.open(DB_NAME, 1);
      request.onupgradeneeded = function (event) {
        const db = request.result;
        // Ensure target store exists
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME, { keyPath: 'key' });
        }
      };
      request.onsuccess = function () {
        resolve(request.result);
      };
      request.onerror = function () {
        reject(request.error || new Error('Failed to open IndexedDB'));
      };
    } catch (err) {
      reject(err);
    }
  });
  return __dbPromise;
}

function withStore(mode, runner) {
  return openDB().then(
    (db) =>
      new Promise((resolve, reject) => {
        try {
          const tx = db.transaction(STORE_NAME, mode);
          const store = tx.objectStore(STORE_NAME);
          const req = runner(store);
          req.onsuccess = () => resolve(req.result);
          req.onerror = () => reject(req.error || new Error('IndexedDB request error'));
        } catch (err) {
          reject(err);
        }
      })
  );
}

async function getItem(key) {
  if (!__hasIDB) throw new Error('IndexedDB not available');
  try {
    if (__cache.has(key)) return __cache.get(key);
    const rec = await withStore('readonly', (store) => store.get(key));
    const val = rec && typeof rec.value !== 'undefined' ? rec.value : null;
    if (val !== null) __cache.set(key, val);
    return val;
  } catch (err) {
    console.error('[idb.getItem] error for key:', key, err);
    return null;
  }
}

async function setItem(key, value) {
  if (!__hasIDB) throw new Error('IndexedDB not available');
  try {
    const val = typeof value === 'string' ? value : String(value);
    __cache.set(key, val);
    await withStore('readwrite', (store) => store.put({ key, value: val, ts: Date.now() }));
  } catch (err) {
    console.error('[idb.setItem] error for key:', key, err);
  }
}

async function removeItem(key) {
  if (!__hasIDB) throw new Error('IndexedDB not available');
  try {
    __cache.delete(key);
    await withStore('readwrite', (store) => store.delete(key));
  } catch (err) {
    console.error('[idb.removeItem] error for key:', key, err);
  }
}

// Synchronous cache facade (non-blocking persistence)
function getItemSync(key) {
  try {
    console.log('[idb.getItemSync] key:', key, __cache?.get(key));
    if (__cache.has(key)) return __cache.get(key);
    // Trigger async hydration for next time; current call returns null if not cached
    getItem(key).catch(() => {});
    return null;
  } catch (err) {
    console.error('[idb.getItemSync] error for key:', key, err);
    return null;
  }
}

function setItemSync(key, value) {
  try {
    const val = typeof value === 'string' ? value : String(value);
    __cache.set(key, val);
    // Fire-and-forget async persist
    setItem(key, val).catch((err) => console.error('[idb.setItemSync->persist] error', err));
  } catch (err) {
    console.error('[idb.setItemSync] error for key:', key, err);
  }
}

function removeItemSync(key) {
  try {
    __cache.delete(key);
    removeItem(key).catch((err) => console.error('[idb.removeItemSync->persist] error', err));
  } catch (err) {
    console.error('[idb.removeItemSync] error for key:', key, err);
  }
}

// JSON helpers
async function getJSON(key) {
  const v = await getItem(key);
  if (v == null) return null;
  try {
    return JSON.parse(v);
  } catch (e) {
    console.error('[idb.getJSON] parse error for key:', key, e);
    return null;
  }
}

function getJSONSync(key) {
  const v = getItemSync(key);
  if (v == null) return null;
  try {
    return JSON.parse(v);
  } catch (e) {
    console.error('[idb.getJSONSync] parse error for key:', key, e);
    return null;
  }
}

async function setJSON(key, obj) {
  try {
    await setItem(key, JSON.stringify(obj));
  } catch (e) {
    console.error('[idb.setJSON] stringify error for key:', key, e);
  }
}

function setJSONSync(key, obj) {
  try {
    setItemSync(key, JSON.stringify(obj));
  } catch (e) {
    console.error('[idb.setJSONSync] stringify error for key:', key, e);
  }
}

// Optional bootstrap: warm cache for commonly used keys
const BOOTSTRAP_KEYS = [
  'screenPath',
  'kioskCtEventData',
  'device_id',
  'session-start-time',
  'esx_internal_auth',
  'UPSELL_DATA',
  'kiosk_device_id',
];

async function bootstrap(keys = BOOTSTRAP_KEYS) {
  if (!__hasIDB) {
    console.error('IndexedDB not available. Skipping bootstrap.');
    return;
  }
  try {
    await openDB();
    // Warm the cache (no migration from localStorage)
    await Promise.all(
      keys.map(async (k) => {
        try {
          const v = await getItem(k);
          if (v != null) __cache.set(k, v);
          console.log('[idb.bootstrap] key:', k, v);
        } catch (e) {
          console.error('[idb.bootstrap] key error:', k, e);
        }
      })
    );
  } catch (err) {
    console.error('[idb.bootstrap] error', err);
  }
}

const idb = {
  // async API
  getItem,
  setItem,
  removeItem,
  getJSON,
  setJSON,
  // sync cache facade
  getItemSync,
  setItemSync,
  removeItemSync,
  getJSONSync,
  setJSONSync,
  // bootstrap/migrate
  bootstrap,
};

export default idb;
